/**
 * Plugin config bodies, rendered on the plugin detail page
 * (`features/plugins/PluginDetail.tsx`):
 *   - CapabilityGlobalInitContent — the capability's `BaseInitAttr` global
 *     default
 *   - PartnerCapabilityInitContent — a federated partner's default
 *
 * All reuse the shared form bodies in ./shared/. The per-org editors on
 * the tenant's Capabilities page target their own endpoints; this surface edits the
 * global layer only.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import {
  deleteCapabilityGlobalConfig,
  getCapabilityGlobalConfig,
  setCapabilityGlobalConfig,
} from '@optum/api-client/endpoints/capability-global-configs';
import { getCapabilitySchema } from '@optum/api-client/endpoints/capabilities';
import {
  deletePartnerCapabilityConfig,
  getPartnerCapabilityConfig,
  setPartnerCapabilityConfig,
} from '@optum/api-client/endpoints/partner-capability-configs';
import { listSecretFields } from '@optum/api-client/endpoints/secret-fields';
import type { FieldScopes, FieldSpec } from '@optum/api-client/types';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';

import { Typography } from '../../ui/finixt.js';
import type { OverridableScope } from './shared/init-config-form.js';
import {
  ConfigFieldRow,
  ScopeChips,
  clearStaleDependents,
  collectViolations,
  filterFrameworkFields,
  sanitizeAgainstSchema,
} from './shared/init-config-form.js';

/** Drop entries whose key is not a current schema field. Stored rows can
 *  carry keys from before a plugin upgrade; echoing them back on save
 *  would trip the strict field_scopes validation (422). */
function filterToSchema<T>(record: Record<string, T>, fields: FieldSpec[]): Record<string, T> {
  const names = new Set(fields.map((f) => f.name));
  return Object.fromEntries(Object.entries(record).filter(([name]) => names.has(name)));
}

// ── Capability global init body ────────────────────────────────────────

export function CapabilityGlobalInitContent({
  capabilityId,
  onSaved,
}: {
  capabilityId: string;
  onSaved?: () => void;
}) {
  const schema = useQuery({
    queryKey: qk.capabilitySchema(capabilityId),
    queryFn: () => getCapabilitySchema(capabilityId),
  });
  const stored = useQuery({
    queryKey: qk.capabilityGlobalConfig(capabilityId),
    queryFn: () => getCapabilityGlobalConfig(capabilityId),
    retry: (n, e) => (e instanceof ApiError && e.status === 404 ? false : n < 2),
  });

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [scopes, setScopes] = useState<FieldScopes>({});
  const toast = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (!schema.data) return;
    if (stored.data) {
      const { cleaned } = sanitizeAgainstSchema(stored.data.config, schema.data.init_fields);
      setValues(filterToSchema(cleaned, schema.data.init_fields));
      setScopes(filterToSchema(stored.data.field_scopes ?? {}, schema.data.init_fields));
    } else {
      setValues({});
      setScopes({});
    }
  }, [stored.data, schema.data]);

  const initFields = useMemo(
    () => (schema.data ? filterFrameworkFields(schema.data.init_fields) : []),
    [schema.data],
  );
  const violations = useMemo(() => collectViolations(values, initFields), [values, initFields]);

  const save = useMutation({
    mutationFn: () =>
      setCapabilityGlobalConfig(capabilityId, {
        config: values,
        field_scopes: scopes,
      }),
    onSuccess: () => {
      toast.success('Global default saved');
      qc.invalidateQueries({
        queryKey: qk.capabilityGlobalConfig(capabilityId),
      });
      qc.invalidateQueries({ queryKey: qk.capabilityGlobalConfigs() });
      onSaved?.();
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? formatApiErr(e) : String(e)),
  });
  const reset = useMutation({
    mutationFn: () => deleteCapabilityGlobalConfig(capabilityId),
    onSuccess: () => {
      toast.success('Global default cleared');
      setValues({});
      setScopes({});
      qc.invalidateQueries({
        queryKey: qk.capabilityGlobalConfig(capabilityId),
      });
      qc.invalidateQueries({ queryKey: qk.capabilityGlobalConfigs() });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 404) {
        setValues({});
        toast.success('Already clean');
        return;
      }
      toast.error('Reset failed', e instanceof ApiError ? formatApiErr(e) : String(e));
    },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Typography variant="card">Global init defaults — {capabilityId}</Typography>
        <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          Layer 2 of the capability InitAttr merge. Each field is locked to this value unless a
          toggle enables a lower layer. A partner that holds its own row takes over the Org and User
          decision for its organizations.
        </p>
      </div>
      <ConfigBody
        schemaPending={schema.isPending}
        schemaError={schema.isError ? formatErr(schema.error) : null}
        storedPending={stored.isPending && !is404(stored.error)}
        initFields={initFields}
        values={values}
        setValues={setValues}
        violations={violations}
        fieldScopes={scopes}
        showPartnerScope
        showUserScope
        onToggleScope={(field, scope) =>
          setScopes((prev) => ({
            ...prev,
            [field]: { ...prev[field], [scope]: prev[field]?.[scope] !== true },
          }))
        }
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
        <Button
          variant="ghost"
          onClick={() => reset.mutate()}
          disabled={reset.isPending || save.isPending}
        >
          {reset.isPending ? 'Resetting…' : 'Reset to defaults'}
        </Button>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || Object.keys(violations).length > 0}
        >
          {save.isPending ? 'Saving…' : 'Save global default'}
        </Button>
      </div>
    </div>
  );
}

// ── Shared init-config body wrapper ────────────────────────────────────

function ConfigBody({
  schemaPending,
  schemaError,
  storedPending,
  initFields,
  values,
  setValues,
  violations,
  fieldScopes,
  showPartnerScope = false,
  showUserScope = false,
  onToggleScope,
}: {
  schemaPending: boolean;
  schemaError: string | null;
  storedPending: boolean;
  initFields: FieldSpec[];
  values: Record<string, unknown>;
  setValues: (v: Record<string, unknown>) => void;
  violations: Record<string, string>;
  fieldScopes?: FieldScopes;
  showPartnerScope?: boolean;
  showUserScope?: boolean;
  onToggleScope?: (field: string, scope: OverridableScope) => void;
}) {
  // Global config is org-less, so the `$secret` picker lists the global
  // secret-field catalogue (keys). Real resolution is deferred to task time
  // against the consuming org's secret store.
  const secretFields = useQuery({
    queryKey: qk.secretFields(),
    queryFn: listSecretFields,
  });
  const secretFieldKeys = useMemo(
    () => (secretFields.data ?? []).map((f) => f.key),
    [secretFields.data],
  );

  if (schemaPending || storedPending) return <LoadingBlock />;
  if (schemaError) return <ErrorBlock message={schemaError} />;
  if (initFields.length === 0) {
    return <EmptyBlock>This plugin has no configurable init fields.</EmptyBlock>;
  }

  return (
    <div
      style={{
        marginTop: 16,
        maxHeight: '55vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        overflowY: 'auto',
        paddingRight: 5,
      }}
    >
      {initFields.map((spec) => (
        <ConfigFieldRow
          key={spec.name}
          spec={spec}
          value={values[spec.name]}
          siblings={values}
          {...(violations[spec.name] !== undefined ? { violation: violations[spec.name]! } : {})}
          secretFieldKeys={secretFieldKeys}
          {...(onToggleScope
            ? {
                footer: (
                  <ScopeChips
                    scopes={fieldScopes?.[spec.name]}
                    showPartner={showPartnerScope}
                    showUser={showUserScope}
                    onToggle={(scope) => onToggleScope(spec.name, scope)}
                  />
                ),
              }
            : {})}
          onChange={(v) => {
            let next = { ...values };
            if (v === undefined) delete next[spec.name];
            else next[spec.name] = v;
            next = clearStaleDependents(initFields, next, spec.name);
            setValues(next);
          }}
        />
      ))}
    </div>
  );
}

function formatErr(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

function is404(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

function formatApiErr(e: ApiError): string {
  const d = e.details as { message?: string; reason?: string; errors?: unknown[] } | undefined;
  if (d?.message) return d.message;
  if (d?.reason) return d.reason;
  return e.message;
}

// ── Partner init bodies (Feature 48) ───────────────────────────────────

/** Shown above the fields when this partner holds no row yet.
 *
 *  Saving creates one, and from that moment the partner's `field_scopes` —
 *  not the global row's — decides what its organizations may override. A
 *  field left out of the map is locked for them. That consequence belongs
 *  where the row is created, not only in the specification.
 */
function GateTakeoverNotice({ organizations }: { organizations: string }) {
  return (
    <p
      style={{
        borderRadius: 'var(--radius-control)',
        border: '1px solid var(--warn-line)',
        borderStyle: 'dashed',
        background: 'var(--warn-tint)',
        paddingLeft: 11,
        paddingRight: 11,
        paddingTop: 7,
        paddingBottom: 7,
        fontSize: 'var(--text-body-md)',
      }}
    >
      This partner has no row for this plugin yet. Saving one makes it the gatekeeper for{' '}
      {organizations}: any field left out of the toggles below becomes locked for them, even where
      the global layer allows it.
    </p>
  );
}

export function PartnerCapabilityInitContent({
  partnerId,
  capabilityId,
  onSaved,
}: {
  partnerId: string;
  capabilityId: string;
  onSaved?: () => void;
}) {
  const schema = useQuery({
    queryKey: qk.capabilitySchema(capabilityId),
    queryFn: () => getCapabilitySchema(capabilityId),
  });
  const stored = useQuery({
    queryKey: qk.partnerCapabilityConfig(partnerId, capabilityId),
    queryFn: () => getPartnerCapabilityConfig(partnerId, capabilityId),
    retry: (n, e) => (e instanceof ApiError && e.status === 404 ? false : n < 2),
  });

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [scopes, setScopes] = useState<FieldScopes>({});
  const toast = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (!schema.data) return;
    if (stored.data) {
      const { cleaned } = sanitizeAgainstSchema(stored.data.config, schema.data.init_fields);
      setValues(filterToSchema(cleaned, schema.data.init_fields));
      setScopes(filterToSchema(stored.data.field_scopes ?? {}, schema.data.init_fields));
    } else {
      setValues({});
      setScopes({});
    }
  }, [stored.data, schema.data]);

  const initFields = useMemo(
    () => (schema.data ? filterFrameworkFields(schema.data.init_fields) : []),
    [schema.data],
  );
  const violations = useMemo(() => collectViolations(values, initFields), [values, initFields]);

  const invalidate = () => {
    qc.invalidateQueries({
      queryKey: qk.partnerCapabilityConfig(partnerId, capabilityId),
    });
    qc.invalidateQueries({ queryKey: qk.partnerCapabilityConfigs(partnerId) });
  };

  const save = useMutation({
    mutationFn: () =>
      setPartnerCapabilityConfig(partnerId, capabilityId, {
        config: values,
        field_scopes: scopes,
      }),
    onSuccess: () => {
      toast.success('Partner default saved');
      invalidate();
      onSaved?.();
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? formatApiErr(e) : String(e)),
  });
  const reset = useMutation({
    mutationFn: () => deletePartnerCapabilityConfig(partnerId, capabilityId),
    onSuccess: () => {
      toast.success('Partner default cleared');
      setValues({});
      setScopes({});
      invalidate();
    },
    onError: (e) =>
      toast.error('Reset failed', e instanceof ApiError ? formatApiErr(e) : String(e)),
  });

  const hasRow = stored.data !== undefined && !is404(stored.error);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Typography variant="card">Partner defaults — {capabilityId}</Typography>
        <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          Layer 3 of the capability InitAttr merge, applying to every organization this partner
          provisioned. A field the global layer does not open to partners is rejected on save.
        </p>
      </div>
      {!hasRow && <GateTakeoverNotice organizations="this partner's organizations" />}
      <ConfigBody
        schemaPending={schema.isPending}
        schemaError={schema.isError ? formatErr(schema.error) : null}
        storedPending={stored.isPending && !is404(stored.error)}
        initFields={initFields}
        values={values}
        setValues={setValues}
        violations={violations}
        fieldScopes={scopes}
        showUserScope
        onToggleScope={(field, scope) =>
          setScopes((prev) => ({
            ...prev,
            [field]: { ...prev[field], [scope]: prev[field]?.[scope] !== true },
          }))
        }
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
        <Button
          variant="ghost"
          onClick={() => reset.mutate()}
          disabled={reset.isPending || save.isPending || !hasRow}
        >
          {reset.isPending ? 'Clearing…' : 'Clear partner row'}
        </Button>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || Object.keys(violations).length > 0}
        >
          {save.isPending ? 'Saving…' : 'Save partner default'}
        </Button>
      </div>
    </div>
  );
}
